import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { User, NewUser } from "models/User";
import { UserService } from "services/UserService";
import { Input, Button, Spinner, CardRadioButtons } from "components";

/**
* AddEditTeamMember Component
* ---------------------------
* A form which is used for both adding and editing team members.
* If the property 'user' is provided, the form will be pre-filled with the user's data, and in edit mode.
* Otherwise, the form will be in add mode.
**/

interface AddEditTeamMemberProps {
  user?: User;
  onClose: () => void;
  onUserAdded: () => void;
  onUserUpdated: () => void;
}

// prettier-ignore
export const AddEditTeamMember: React.FC<AddEditTeamMemberProps> = ({ user, onClose, onUserAdded, onUserUpdated }) => {
  
  const isEditMode = !!user;
  const userService = new UserService();
  
  // prettier-ignore - Creating form values
  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting }} = useForm<NewUser>({
    defaultValues: {
      email: user?.email ?? "",
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      fkDistributorId: user?.fkDistributorId ?? "",
      active: user?.active ?? true,
      role: user?.role ?? "Member",
    },
  });

  // Role options for the select component
  const roleOptions: any[] = [
    {
      label: "Member",
      value: "Member",
      description: "Standard workspace access",
    },
    {
      label: "Admin",
      value: "Admin",
      description: "Manage users and settings",
    },
  ];

  

  // Watch the role value for the select component
  const selectedRole = watch("role");

  // Handle role selection
  const handleRoleChange = (value: string | number) => {
    setValue("role", value.toString(), { shouldValidate: true });
  };

  // Register role field for validation
  register("role", { required: "Role is required" });

  // Submit form
  const onSubmit: SubmitHandler<NewUser> = async (userForm) => {
    try {

      if (isEditMode && user?.id) {
        await userService.updateUser({ ...userForm, id: user.id });
        onUserUpdated();
      } 
      else {
        await userService.createUser(userForm);
        onUserAdded();
      }
      onClose();
    } catch (error) {
      console.error("Failed to submit user form", error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex gap-2">
          <Input
            label="First name"
            placeholder="First name"
            {...register("firstName")}
            error={errors.firstName?.message as string | undefined}
          />
          <Input
            label="Last name"
            placeholder="Last name"
            {...register("lastName")}
            error={errors.lastName?.message as string | undefined}
          />
        </div>

        <Input
          label="Email"
          placeholder="email@example.com"
          type="email"
          {...register("email", { required: "Email is required" })}
          error={errors.email?.message as string | undefined}
        />

        <div className="flex flex-col">
          <label className="mb-1 text-sm font-medium text-neutral-700">
            Role
          </label>
          <CardRadioButtons
            options={roleOptions}
            value={selectedRole}
            onChange={handleRoleChange}
          />
          {errors.role?.message && (
            <small className="mt-1 text-xs text-red-500">
              {errors.role.message}
            </small>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end space-x-3">
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Spinner className="mr-2" />}
          {isSubmitting
            ? isEditMode
              ? "Updating user…"
              : "Adding user…"
            : isEditMode
            ? "Save changes"
            : "Add user"}
          
        </Button>
      </div>
    </form>
  );
};
